import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { deriveCarrierStatus } from './deriveCarrierStatus';
import { TESTED_CARRIER_VALUES } from './constants';

const EMPTY = Object.freeze({
  byMember: {},
  presentStatesByDisease: {},
  diseaseColorMap: {}
});

// Family diseases = union of every diagnosis present across the pedigree. Each
// diagnosis carries its colour and an `alias` (often an OMIM code) used to match
// gene→disease associations from the terminology service.
function buildFamilyDiseases(members) {
  const byId = new Map();
  for (const m of members || []) {
    for (const dx of m.diagnoses || []) {
      if (!dx || dx.id == null || byId.has(dx.id)) continue;
      byId.set(dx.id, {
        id: dx.id,
        color: dx.color,
        name: dx.name,
        externalIds: [dx.alias, dx.id].filter(Boolean)
      });
    }
  }
  return [...byId.values()];
}

function collectCarrierGenes(members) {
  const genes = new Set();
  for (const m of members || []) {
    for (const g of m.genotypes || []) {
      const status = String((g && g.carrierStatus) || '').trim();
      const sym = String((g && (g.symbol || g.gene)) || '').trim();
      if (sym && TESTED_CARRIER_VALUES.has(status)) genes.add(sym);
    }
  }
  return [...genes].sort();
}

// Projects carrier status onto the pedigree at render time. Fetches the
// gene→disease map (cached per gene) for the family's asserted-carrier genes,
// then runs the pure deriveCarrierStatus projection. Returns EMPTY when the
// feature is disabled so callers can treat "off" uniformly.
export function useCarrierStatus({
  members,
  familyDiseases: familyDiseasesProp,
  enabled = true,
  conditionCarrierEnabled = false
}) {
  const [geneDiseaseMap, setGeneDiseaseMap] = useState({});
  // Genes whose gene-disease lookup did not answer. Surfaced so the chart can
  // say the projection is incomplete instead of silently drawing no carriers.
  const [unresolvedGenes, setUnresolvedGenes] = useState([]);
  const cacheRef = useRef(new Map()); // geneSymbol -> [diseaseId]

  const genes = useMemo(
    () => (enabled ? collectCarrierGenes(members) : []),
    [members, enabled]
  );
  const genesKey = genes.join(',');

  useEffect(() => {
    if (!enabled || genes.length === 0) {
      setGeneDiseaseMap({});
      setUnresolvedGenes([]);
      return undefined;
    }
    let cancelled = false;
    const missing = genes.filter((g) => !cacheRef.current.has(g));

    // Genes whose lookup did not answer this round. Reported out of the hook
    // so the chart can say "carrier status incomplete" rather than implying a
    // negative.
    const unresolved = [];

    (async () => {
      await Promise.all(
        missing.map(async (gene) => {
          try {
            // expand=xrefs folds in curated disease cross-references so a gene
            // (e.g. BRCA1 → OMIM:604370) also matches a family disease recorded
            // under a related code (e.g. OMIM:167000 "Ovarian cancer").
            const url =
              APICONSTANTS.geneDiseaseByGene.replace(
                ':geneSymbol',
                encodeURIComponent(gene)
              ) + '?expand=xrefs';
            const { data } = await axios.get(url);
            // An unanswered lookup must NOT be cached as "this gene has no
            // associated diseases". `missing` is computed as
            // `genes.filter(g => !cache.has(g))`, so a gene poisoned with []
            // was never retried while the component stayed mounted — and the
            // pedigree then drew a clean, complete-looking chart in which
            // nobody is a tested carrier. A false negative rendered as fact
            // (TGCLOUD-1785).
            if (data?.available === false) {
              unresolved.push(gene);
              return;
            }
            cacheRef.current.set(
              gene,
              (data?.diseases || []).map((d) => d.id).filter(Boolean)
            );
          } catch (_) {
            // Leave the gene OUT of the cache so the next render retries it.
            unresolved.push(gene);
          }
        })
      );
      if (cancelled) return;
      const map = {};
      // Only genes with a real answer enter the map. A gene absent from the
      // cache stays absent, so downstream can tell "no associated diseases"
      // from "not looked up".
      for (const g of genes) {
        if (cacheRef.current.has(g)) map[g] = cacheRef.current.get(g) || [];
      }
      setGeneDiseaseMap(map);
      setUnresolvedGenes(unresolved);
    })();

    return () => {
      cancelled = true;
    };
  }, [genesKey, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  return useMemo(() => {
    if (!enabled) return EMPTY;
    // The canonical family disease list is authoritative — it carries every tracked disease,
    // including ones nobody is diagnosed with (e.g. ovarian when only the gene is present), with
    // server-assigned colours. But it is gathered from MEMBERS, so a disease that reaches the render
    // by another route resolved no colour and painted '#000'.
    //
    // That is now a UNION rather than an either/or. The case that forced it is a fetus diagnosis
    // (decision 20): it is stored inside a pregnancy record, so the server never sees it, yet
    // deriveReproNodes exposes it on the render node — where the shading pass needs a colour. Since
    // the derived nodes are part of `members` here, picking up what the canonical list is missing
    // covers fetuses without any fetus-specific code, and closes the same hole for anything else.
    // Canonical entries win on conflict, so a server-assigned colour is never overwritten.
    const canonical = (familyDiseasesProp || []).map((d) => ({
      id: d.id,
      color: d.color,
      name: d.name,
      externalIds: [d.alias, d.id].filter(Boolean)
    }));
    const known = new Set(canonical.map((d) => String(d.id)));
    const fromMembers = buildFamilyDiseases(members).filter(
      (d) => !known.has(String(d.id))
    );
    const familyDiseases = fromMembers.length
      ? [...canonical, ...fromMembers]
      : canonical.length
      ? canonical
      : buildFamilyDiseases(members);
    const diseaseColorMap = {};
    for (const d of familyDiseases) {
      diseaseColorMap[d.id] = { color: d.color, name: d.name };
    }
    const { byMember, presentStatesByDisease } = deriveCarrierStatus({
      members,
      familyDiseases,
      geneDiseaseMap,
      conditionCarrierEnabled
    });
    return {
      byMember,
      presentStatesByDisease,
      diseaseColorMap,
      // Non-empty means at least one gene's disease associations could not be
      // resolved, so an absent tested-carrier mark may be a missing lookup
      // rather than a negative finding.
      unresolvedGenes
    };
  }, [
    members,
    familyDiseasesProp,
    geneDiseaseMap,
    unresolvedGenes,
    enabled,
    conditionCarrierEnabled
  ]);
}
